import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  ExportDropdown,
  Hours,
  InternalClientTooltip,
  Percent,
  RouteLink,
  Widget,
  YesNoFilter,
} from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersGroup,
  ReportPeriodFilter,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useClientFilters,
  useDocumentTitle,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import colors from '~/styles/colors';
import weights from '~/styles/weights';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Meter from '../components/Meter';
import { Metric, Metrics } from '../components/Metric';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const ClientInfo = styled.div`
  line-height: 1;
`;

const Member = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

export default function TimeSummaryByClientAndMember({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const memberFilters = useMemberFilters();
  const roleFilters = useProjectRoleFilters();
  const timeFilters = useTimeFilters();

  const [params, setParams] = useState({
    period: null,
    includeMembersWithoutTime: false,
    sort: new QuerySort('client.name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...memberFilters.filters,
    ...roleFilters.filters,
    ...timeFilters.filters,
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        includeMembersWithoutTime: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },
        sort: { default: new QuerySort('client.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        memberFilters,
        timeFilters,
        roleFilters,
      ],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      includeMembersWithoutTime: params.includeMembersWithoutTime ? 'true' : undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, roleFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.pdf);
                    setIsOpen(false);
                  }}>
                  Export to PDF
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter value={params.period} onChange={({ value }) => handleApplyFilters({ period: value })} />

        <YesNoFilter
          icon="filter"
          placeholder="Include members without time"
          value={params.includeMembersWithoutTime ? 'yes' : 'no'}
          onChange={({ target: { value } }) => handleApplyFilters({ includeMembersWithoutTime: value === 'yes' })}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const timeDetail = useTimeEntriesReportURL({ params });

  return (
    <>
      <Report.Summary>
        <Widget>
          <Meter>
            <Meter.Value
              label="Client Billable"
              value={report.totals.billable}
              color={colors.success}
              tooltip={
                <>
                  <Hours value={report.totals.billable} /> hours (
                  <Percent value={report.totals.billable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Client Non-Billable"
              value={report.totals.nonBillable}
              color={colors.danger}
              tooltip={
                <>
                  <Hours value={report.totals.nonBillable} /> hours (
                  <Percent value={report.totals.nonBillable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Internal"
              value={report.totals.internal}
              color={colors.warning}
              tooltip={
                <>
                  <Hours value={report.totals.internal} /> hours (
                  <Percent value={report.totals.internal / report.totals.total} />)
                </>
              }
            />
          </Meter>

          <Metrics>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'billable' })}>
                <Metric.Value>
                  <Hours value={report.totals.billable} />
                </Metric.Value>
                <Metric.Label>Client Billable</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'non_billable' })}>
                <Metric.Value>
                  <Hours value={report.totals.nonBillable} />
                </Metric.Value>
                <Metric.Label>Client Non-Billable</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'internal' })}>
                <Metric.Value>
                  <Hours value={report.totals.internal} />
                </Metric.Value>
                <Metric.Label>Internal</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: ['billable', 'non_billable', 'internal'] })}>
                <Metric.Value>
                  <Hours value={report.totals.total} />
                </Metric.Value>
                <Metric.Label>Total</Metric.Label>
              </RouteLink>
            </Metric>
          </Metrics>
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="client.name" onSort={onSort} sort={params.sort}>
              Client
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Client
              <br />
              Billable
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Client
              <br />
              Non-Billable
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Internal
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.status === 'sorting'}>
            {report.records.map((client) => {
              return (
                <React.Fragment key={client.id}>
                  <Table.GroupRow top="4.075rem">
                    <Table.Cell>
                      <ClientInfo>
                        <p style={{ display: 'flex' }}>
                          <ClientLink client={client} />
                          {client.isInternal && <InternalClientTooltip />}
                        </p>
                      </ClientInfo>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ clientId: client.id, timeBillableTypeId: 'billable' })}>
                          <Hours value={client.billableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ clientId: client.id, timeBillableTypeId: 'non_billable' })}>
                          <Hours value={client.nonBillableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ clientId: client.id, timeBillableTypeId: 'internal' })}>
                          <Hours value={client.internalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ clientId: client.id })}>
                          <Hours value={client.totalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                  </Table.GroupRow>

                  {client.members.map((projectMember) => {
                    return (
                      <Table.Row key={projectMember.member.id}>
                        <Table.Cell>
                          <Member>{projectMember.member.name}</Member>
                        </Table.Cell>
                        <Table.Cell>
                          <RouteLink
                            to={timeDetail({
                              clientId: client.id,
                              memberId: projectMember.member.id,
                              timeBillableTypeId: 'billable',
                            })}>
                            <Hours value={projectMember.billableHours} />
                          </RouteLink>
                        </Table.Cell>
                        <Table.Cell>
                          <RouteLink
                            to={timeDetail({
                              clientId: client.id,
                              memberId: projectMember.member.id,
                              timeBillableTypeId: 'non_billable',
                            })}>
                            <Hours value={projectMember.nonBillableHours} />
                          </RouteLink>
                        </Table.Cell>
                        <Table.Cell>
                          <RouteLink
                            to={timeDetail({
                              clientId: client.id,
                              memberId: projectMember.member.id,
                              timeBillableTypeId: 'internal',
                            })}>
                            <Hours value={projectMember.internalHours} />
                          </RouteLink>
                        </Table.Cell>
                        <Table.Cell>
                          <RouteLink to={timeDetail({ clientId: client.id, memberId: projectMember.member.id })}>
                            <Hours value={projectMember.totalHours} />
                          </RouteLink>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell>
                <RouteLink
                  to={timeDetail({
                    timeBillableTypeId: 'billable',
                  })}>
                  <Hours value={report.totals.billable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink
                  to={timeDetail({
                    timeBillableTypeId: 'non_billable',
                  })}>
                  <Hours value={report.totals.nonBillable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink
                  to={timeDetail({
                    timeBillableTypeId: 'internal',
                  })}>
                  <Hours value={report.totals.internal} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: ['billable', 'non_billable', 'internal'] })}>
                  <Hours value={report.totals.total} />
                </RouteLink>
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label="Client"
            isLoading={query.status !== 'ready'}
            partial={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <YesNoFilter
        icon="filter"
        placeholder="Include members without tracked time"
        value={filters.includeMembersWithoutTime ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ includeMembersWithoutTime: value === 'yes' })}
      />

      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
